import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';

import { formatEuroAmount } from 'helpers/price';
import { formatDateForDisplay } from 'helpers/dateTimes';

import {
    getExpectedQuantity,
    getProductLinePrice,
    SupplyOrderDetails,
    SupplyOrderFormValues,
} from '../supplyOrderFormValues';
import { getSupplyOrderProductReceivedTotal } from '../../shared/receivedDetails';
import { SupplierDeliveryChronology } from './SupplierDeliveryChronology';

/**
 * Les informations qu'on vient chercher en ouvrant un achat : chez qui, pour quand, pour combien, où en
 * est le rapprochement Odoo, et la ponctualité passée du fournisseur. Tout le reste de la fiche est du
 * second plan. Une seule carte : ces deux blocs se lisent ensemble, pas l'un après l'autre.
 */
export const SupplyOrderKeyFacts = ({ supplyOrder }: { supplyOrder: SupplyOrderDetails }) => {
    const { control } = useFormContext<SupplyOrderFormValues>();
    const products = useWatch({ control, name: 'products' }) ?? [];

    const totalHT = products.reduce((total, product) => total + getProductLinePrice(product), 0);
    const expectedQuantity = products.reduce((total, product) => total + getExpectedQuantity(product), 0);
    const receivedQuantity = products.reduce(
        (total, product) =>
            total + (getSupplyOrderProductReceivedTotal(product.productReceivedDetails).quantityReceived ?? 0),
        0,
    );
    const orderedLinesCount = products.filter(({ colis, confirmedQuantity }) => colis || confirmedQuantity).length;

    return (
        <div
            className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3"
            data-test="supply-order-key-facts"
        >
            <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-6 gap-y-4">
                <Fact label="Fournisseur">
                    <Link
                        className="font-medium text-primary hover:underline"
                        to={`/supplier/${supplyOrder.supplier._id}`}
                        data-test="supply-order-supplier-link"
                    >
                        {supplyOrder.supplier.name}
                    </Link>
                </Fact>
                <Fact label="Entrepôt">{supplyOrder.warehouseColumn?.name ?? 'N/A'}</Fact>
                <Fact label="Commandé le">{formatDate(supplyOrder.orderDate)}</Fact>
                <Fact label="Livraison prévue">
                    <span className={supplyOrder.scheduledDeliveryDateValidity ? undefined : 'text-destructive'}>
                        {formatDate(supplyOrder.dateDeliveryScheduled)}
                    </span>
                </Fact>
                <Fact label="Livré le">{formatDate(supplyOrder.dateDelivery)}</Fact>
                <Fact label="Produits commandés">{orderedLinesCount}</Fact>
                <Fact label="Quantité attendue / reçue">
                    {expectedQuantity} <span className="text-muted-foreground">/</span> {receivedQuantity}
                </Fact>
                <Fact label="Total HT">
                    <span className="font-semibold" data-test="supply-order-total-ht">
                        {formatEuroAmount(totalHT)}
                    </span>
                </Fact>
                <Fact label="Bon de commande Odoo">
                    <OdooLink
                        url={supplyOrder.odooPurchaseOrderUrl}
                        label={supplyOrder.odooPurchaseOrderName}
                        emptyLabel="Pas encore de bon de commande"
                        dataTest="supply-order-odoo-purchase-order"
                    />
                </Fact>
                <Fact label="Bon de réception Odoo">
                    <OdooLink
                        url={supplyOrder.odooReceiptPickingUrl}
                        label="Voir le bon de réception"
                        emptyLabel="Pas encore de bon de réception"
                        dataTest="supply-order-odoo-receipt"
                    />
                </Fact>
            </div>

            <SupplierDeliveryChronology supplyOrder={supplyOrder} />
        </div>
    );
};

function formatDate(date?: string | null) {
    return date ? formatDateForDisplay(date) : <span className="text-muted-foreground">—</span>;
}

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="truncate text-sm">{children}</span>
    </div>
);

const OdooLink = ({
    url,
    label,
    emptyLabel,
    dataTest,
}: {
    url?: string | null;
    label?: string | null;
    emptyLabel: string;
    dataTest: string;
}) => {
    if (!url) {
        return (
            <span className="text-muted-foreground" data-test={dataTest}>
                {emptyLabel}
            </span>
        );
    }

    return (
        <a
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-test={dataTest}
        >
            {label ?? 'Ouvrir dans Odoo'}
            <ExternalLink className="size-3" />
        </a>
    );
};
